import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, ShieldCheck, User, RefreshCw } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

/**
 * AppReviewDemoBadge
 * ------------------
 * Floating, draggable widget that ONLY appears when the signed-in user
 * is the App Store Reviewer demo account (`info.niafinance@gmail.com`).
 *
 * For every other user, the widget renders nothing and adds zero
 * runtime cost.
 *
 * Top-fintech reviewer-mode pattern (Cash App / Wise / Revolut style):
 *   * Make the reviewer's escape hatch unmissable - they only have a
 *     few minutes to evaluate the app.
 *   * Let them swap between Sender and Admin views without re-logging.
 *   * Use a "DEMO" label so QA confusion never reaches engineering.
 *   * Provide a one-tap "Reset demo data" so reviewers can replay flows.
 *
 * The widget is purely client-side once the auth context confirms the
 * demo flag. Persona changes are persisted server-side via
 * /api/auth/demo/switch-persona so the choice survives reloads.
 */
export default function AppReviewDemoBadge() {
    const { user, refreshUser, loading } = useAuth();
    const navigate = useNavigate();
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);

    // Hard guard: render nothing for any non-demo session. Auth-loading
    // is also a no-op so we don't flash the badge during cold boot.
    if (loading) return null;
    if (!user?.is_demo) return null;

    const persona = (user?.demo_persona || 'sender').toLowerCase();

    const switchPersona = async (next) => {
        if (busy || next === persona) return;
        setBusy(true);
        try {
            await api.demoSwitchPersona(next);
            await refreshUser();
            toast.success(next === 'admin' ? 'Switched to Admin Demo' : 'Switched to Sender Demo');
            navigate(next === 'admin' ? '/admin/overview' : '/home');
            setOpen(false);
        } catch (e) {
            toast.error('Could not switch persona. Please try again.');
        } finally {
            setBusy(false);
        }
    };

    const refreshDemo = async () => {
        if (busy) return;
        setBusy(true);
        try {
            // Re-seed demo data by calling the verify-otp bypass again. The
            // server seeds idempotently on every successful bypass call.
            await api.verifyOtp({
                channel: 'email',
                identifier: 'info.niafinance@gmail.com',
                code: '424242',
                role: 'sender',
            });
            await refreshUser();
            toast.success('Demo data reset.');
        } catch (e) {
            toast.error('Reset failed. Please log out and back in.');
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="app-review-demo-badge"
            className="app-review-demo-badge"
            style={{
                position: 'fixed',
                right: 12,
                bottom: 92, // sits above the bottom nav on sender; ignored on admin
                zIndex: 9000,
                fontFamily: 'inherit',
            }}
        >
            {open ? (
                <div
                    style={{
                        background: 'rgba(10, 46, 40, 0.97)',
                        color: '#fff',
                        borderRadius: 16,
                        boxShadow: '0 12px 30px rgba(0,0,0,0.25)',
                        padding: '14px 14px 12px',
                        minWidth: 244,
                        backdropFilter: 'blur(10px)',
                    }}
                >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
                        <div style={{ fontWeight: 900, fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#FFD773' }}>
                            App Review Demo
                        </div>
                        <button
                            onClick={() => setOpen(false)}
                            aria-label="Close demo switcher"
                            data-testid="demo-badge-close"
                            style={{ background: 'transparent', border: 'none', color: '#C2DDD3', fontSize: 18, lineHeight: 1, cursor: 'pointer' }}
                        >
                            &times;
                        </button>
                    </div>
                    <div style={{ fontSize: 11, color: '#C2DDD3', marginBottom: 12, lineHeight: 1.45 }}>
                        Signed in as the reviewer account. No real SMS or payments are triggered.
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginBottom: 10 }}>
                        <PersonaPill
                            active={persona === 'sender'}
                            disabled={busy}
                            onClick={() => switchPersona('sender')}
                            icon={<User size={13} />}
                            label="Sender"
                            testid="demo-persona-sender"
                        />
                        <PersonaPill
                            active={persona === 'admin'}
                            disabled={busy}
                            onClick={() => switchPersona('admin')}
                            icon={<ShieldCheck size={13} />}
                            label="Admin"
                            testid="demo-persona-admin"
                        />
                    </div>
                    <button
                        onClick={refreshDemo}
                        disabled={busy}
                        data-testid="demo-reset-data"
                        style={{
                            width: '100%',
                            background: 'rgba(255,255,255,0.10)',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.18)',
                            borderRadius: 10,
                            padding: '8px 10px',
                            fontSize: 12,
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 6,
                            cursor: busy ? 'wait' : 'pointer',
                            opacity: busy ? 0.7 : 1,
                        }}
                    >
                        <RefreshCw size={12} />
                        Reset demo data
                    </button>
                </div>
            ) : (
                <button
                    onClick={() => setOpen(true)}
                    data-testid="demo-badge-toggle"
                    aria-label="Open App Review demo switcher"
                    style={{
                        background: 'linear-gradient(135deg, #FFD773 0%, #F5A623 100%)',
                        color: '#0A2E28',
                        borderRadius: 999,
                        border: 'none',
                        padding: '9px 14px',
                        fontWeight: 900,
                        fontSize: 12,
                        letterSpacing: '0.04em',
                        boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        cursor: 'pointer',
                        textTransform: 'uppercase',
                    }}
                >
                    <Eye size={13} />
                    Demo &middot; {persona === 'admin' ? 'Admin' : 'Sender'}
                </button>
            )}
        </div>
    );
}

function PersonaPill({ active, onClick, icon, label, testid, disabled }) {
    return (
        <button
            onClick={onClick}
            disabled={disabled}
            data-testid={testid}
            style={{
                flex: 1,
                background: active ? '#FFD773' : 'rgba(255,255,255,0.08)',
                color: active ? '#0A2E28' : '#fff',
                border: active ? '1px solid #FFD773' : '1px solid rgba(255,255,255,0.16)',
                borderRadius: 10,
                padding: '8px 10px',
                fontSize: 11,
                fontWeight: 800,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 5,
                cursor: disabled ? 'wait' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
            }}
        >
            {icon}
            {label}
        </button>
    );
}
